// brainSearch.js — E3-WS1-T02 (refactor-deepening / F3, brain-search domain).
//
// Deep module: the vault/brain SEARCH ENGINE that the agent's `search_brain` /
// `semantic_search` tools use — extracted verbatim from agentTools.js (which was a
// 1475-LOC monolith mixing tool schemas, the runTool dispatcher, AND this ~390-line
// engine). Small public interface (runBrainSearch + the format*/filter* helpers the tools
// call); large internal implementation (frontmatter cleanup, wikilink graph traversal,
// RRF-style ranking, synthesis formatting). Deletion test: delete this and the ranking/
// graph/synthesis/format logic scatters back into agentTools — a cohesive domain behind
// a small surface → DEEP.
//
// PURE (no window): every IPC arrives via the injected `api` param. Self-contained — a
// private `truncate` keeps it free of any back-import from agentTools (no cycle).

function truncate(s, max) {
  const str = String(s ?? '');
  return str.length > max ? `${str.slice(0, max)}\n…[truncado, ${str.length} chars total]` : str;
}

// Strip a leading YAML frontmatter block (--- ... ---) and collapse runs of blank
// lines. Raw Obsidian note text carries frontmatter + whitespace noise that burns
// the model's context budget without adding signal. PURE.
const FRONTMATTER_RE = new RegExp(`^\\uFEFF?\\s*---\\r?\\n[\\s\\S]*?\\r?\\n---\\r?\\n?`);
function cleanBrainSnippet(raw) {
  let s = String(raw ?? '');
  // Drop a leading frontmatter fence (only at the top; tolerates a leading BOM).
  s = s.replace(FRONTMATTER_RE, '');
  // Collapse 2+ blank lines into a single blank line; trim trailing space per line.
  s = s.replace(/[ \t]+$/gm, '').replace(/(\r?\n){3,}/g, '\n\n');
  return s.trim();
}

// ── Graph-augmented brain retrieval (feat/brain-graph-search) ───────────────
// Grounded in 3 researched repos: gbrain (wikilink edges → multi-hop traversal +
// reciprocal-rank fusion + "honest note on what the brain doesn't know yet"),
// graphify (1-hop neighbor expansion BEYOND direct [[links]]), Understand-Anything
// (avoid repeated queries). Fixes the "cx2 flail": a narrow/empty brain hit was
// unactionable, so the agent re-queried the SAME thing many times.

// extractWikilinks(text) → unique note targets from [[Note]], [[note|alias]],
// [[note#heading]] (alias + heading stripped). PURE. Caps at 12, [] for non-string.
const WIKILINK_RE = /\[\[([^\]]+)\]\]/g;
export function extractWikilinks(text) {
  if (typeof text !== 'string' || !text) return [];
  const out = [];
  const seen = new Set();
  let m;
  WIKILINK_RE.lastIndex = 0;
  while ((m = WIKILINK_RE.exec(text)) !== null) {
    // Target = part before a `|` alias or a `#heading` anchor, trimmed.
    const target = String(m[1]).split('|')[0].split('#')[0].trim();
    if (!target) continue;
    const key = target.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(target);
    if (out.length >= 12) break;
  }
  return out;
}

// buildBrainSteering(query) → the anti-flail off-ramp. When the brain has NO
// usable hit, return a short (2-4 line) NON-EMPTY instruction that (a) admits no
// exact match for "<query>" (gbrain's knowledge-gap honesty) and (b) tells the
// model to BROADEN/REPHRASE, pick a related note, or proceed/create — rather than
// repeating the SAME query (Understand-Anything's anti-repeat). PURE.
export function buildBrainSteering(query) {
  const q = String(query ?? '').trim() || '(busca vazia)';
  return [
    `Nenhuma nota com match exato no brain para "${q}".`,
    'Não repita a MESMA busca — isso não vai trazer resultado novo.',
    'Em vez disso: reformule/amplie a query, busque um tópico relacionado, ou siga em frente (crie a nota/responda com o que já tem).',
  ].join('\n');
}

// isNothingFoundSentinel(text) → true when an obsidian_get_context body is the
// vault's "nothing found" message rather than real nearby context. The vault
// emits e.g. "Nenhum contexto anterior encontrado para …"; some bridges say
// "no context"/"not found". Treat those as EMPTY so we don't label a no-op as
// "notas próximas". PURE.
const NOTHING_FOUND_RE = /nenhum contexto|no context|not found/i;
function isNothingFoundSentinel(text) {
  return NOTHING_FOUND_RE.test(String(text ?? ''));
}

// rankBrainBlocks(primaryBlocks, neighborBlocks) → joined text with reciprocal-
// rank-fusion-lite ordering: primary hits ALWAYS outrank neighbors, and a neighbor
// duplicating a primary (by key = path|title) is dropped. Blocks are { key, text }.
// PURE.
export function rankBrainBlocks(primaryBlocks = [], neighborBlocks = []) {
  const seen = new Set();
  const ordered = [];
  for (const b of primaryBlocks) {
    if (!b || !b.text) continue;
    const key = String(b.key || '').trim().toLowerCase();
    if (key) seen.add(key);
    ordered.push(b.text);
  }
  for (const b of neighborBlocks) {
    if (!b || !b.text) continue;
    const key = String(b.key || '').trim().toLowerCase();
    if (key && seen.has(key)) continue; // neighbor already covered by a primary
    if (key) seen.add(key);
    ordered.push(b.text);
  }
  return ordered.join('\n\n');
}

// Parse an obsidian_search { results } envelope into STRUCTURED hits we can both
// render AND expand (need body+path for one-hop traversal). Mirrors the cleaning
// formatBrainResult does, but keeps fields instead of flattening to a string.
function parseBrainHits(res) {
  if (!res || !Array.isArray(res.results)) return [];
  const seen = new Set();
  const hits = [];
  for (const r of res.results) {
    if (!r) continue;
    const title = r.title || r.path || '?';
    const path = r.path || r.title || '';
    const key = String(path || title).trim().toLowerCase();
    if (key && seen.has(key)) continue;
    const body = cleanBrainSnippet(r.summary || r.text || '');
    if (!body) continue;
    if (key) seen.add(key);
    hits.push({ title, path, key, body, date: r.date });
  }
  return hits;
}

// Pull a short cleaned excerpt out of whatever obsidian_read_note / get_context /
// obsidian_search returns. The REAL live obsidian MCP shapes (verified against
// Pedro's vault):
//   - obsidian_read_note → PLAIN OBJECT { content: "<full note body STRING>",
//     summary: "<string>", ... }. `content` is a STRING (not an array).
//   - obsidian_get_context → a plain markdown STRING.
//   - obsidian_search → { results: [{ summary, ... }] }.
// We keep the historical { content:[{text}] } and { text } branches too (other
// bridges still emit them). Order of preference (first non-empty wins):
//   string res → content[] array → content string → results[] → text string →
//   summary string. Bounded per arg. Exported for unit tests.
export function brainExcerpt(res, cap = 400) {
  if (res == null) return '';
  let text = '';
  if (typeof res === 'string') text = res;
  else if (Array.isArray(res.content)) {
    text = res.content.map((b) => (typeof b === 'string' ? b : b?.text || '')).filter(Boolean).join('\n');
  } else if (typeof res.content === 'string') text = res.content;
  else if (Array.isArray(res.results)) {
    text = res.results.map((r) => r?.summary || r?.text || '').filter(Boolean).join('\n');
  } else if (typeof res.text === 'string') text = res.text;
  else if (typeof res.summary === 'string') text = res.summary;
  const clean = cleanBrainSnippet(text);
  return clean ? truncate(clean, cap) : '';
}

// Minimum obsidian_search score to TRUST a fuzzy wikilink resolution. Below it,
// we only accept the top result if its basename/title NAME-matches the wikilink
// target (exact, or one startsWith the other). This kills the real-vault trap
// where a DATED-FILENAME wikilink (`[[2026-05-26 - apolo-v11-...]]`) fuzzy-resolves
// to a DIFFERENT, unrelated note (score 9) that then gets injected as "related".
const BRAIN_LINK_CONFIDENCE_MIN = 40;

// isConfidentResolution(top, name) → true when the obsidian_search top result is
// a TRUSTWORTHY resolution of the wikilink `name`: either a high score, or a NAME
// match (basename-sans-.md / title equals `name`, or one is a prefix of the other,
// lowercased+trimmed). PURE. Defensive against missing fields.
function isConfidentResolution(top, name) {
  if (!top) return false;
  const score = Number(top.score);
  if (Number.isFinite(score) && score >= BRAIN_LINK_CONFIDENCE_MIN) return true;
  const want = String(name || '').trim().toLowerCase();
  if (!want) return false;
  const candidates = [pathBasenameKey(top.path), String(top.title || '').trim().toLowerCase()];
  for (const cand of candidates) {
    if (!cand) continue;
    if (cand === want) return true;
    if (cand.startsWith(want) || want.startsWith(cand)) return true;
  }
  return false;
}

// Defensively read a single linked note for one-hop graph expansion. Vault
// wikilink targets are DATED FILENAMES (e.g. `2026-05-24 - foo-sprint|Alias`),
// NOT absolute paths — and obsidian_read_note's schema REQUIRES an absolute
// `path`. So passing the filename to read_note always fails. Instead we RESOLVE
// the filename via obsidian_search FIRST (1 call) → take the top result and use
// its `summary` as the excerpt. If that top result carries an absolute `.path`,
// we MAY do ONE obsidian_read_note({ path }) to pull the fuller `content` body,
// preferring it over the summary. LEAN (1–2 MCP calls), DEFENSIVE (never throws,
// returns '' on any failure). Bounded by the caller's ≤5-neighbor / 400-char caps.
// CONFIDENCE-FILTERED: a low-score fuzzy mismatch (no name match) is DROPPED ('')
// so we never inject the WRONG note as a "related" neighbor. Exported for tests.
export async function readLinkedNote(api, name) {
  let top;
  try {
    const res = await api.mcp.call('obsidian', 'obsidian_search', { query: name, limit: 1 });
    top = res && Array.isArray(res.results) ? res.results[0] : null;
  } catch { return ''; }
  if (!top) return '';

  // GAP 2: drop a low-confidence fuzzy resolution (wrong note) before injecting it.
  if (!isConfidentResolution(top, name)) return '';

  const summaryExcerpt = brainExcerpt({ summary: top.summary || top.text || '' }, 400);

  // Optional second hop: a fuller body from read_note(path) beats the summary.
  if (top.path) {
    try {
      const noteRes = await api.mcp.call('obsidian', 'obsidian_read_note', { path: top.path });
      const bodyExcerpt = brainExcerpt(noteRes, 400);
      if (bodyExcerpt) return bodyExcerpt;
    } catch { /* fall back to the summary excerpt below */ }
  }
  return summaryExcerpt;
}

// Derive a name-comparable key from a note path: drop folders + a trailing .md,
// lowercase, trim. So 'brain/notas/Outra.md' → 'outra', which matches a [[Outra]]
// wikilink target lowercased. PURE. (I1: path-keys never matched name-keys, so
// cross-tier dedup was dead on real vault data.)
function pathBasenameKey(path) {
  const base = String(path || '').split('/').pop() || '';
  return base.replace(/\.md$/i, '').trim().toLowerCase();
}

// formatBrainSynthesis(syn, { cap }) — formata a síntese local (SYN1) pro
// resultado do tool: resposta + citações (paths reais) + lacunas. Bounded.
export function formatBrainSynthesis(syn, { cap = 4000 } = {}) {
  const lines = ["🧠 Pedro's Brain — síntese (modelo local, nada saiu do seu Mac):", '', String(syn.synthesis || '').trim()];
  if (Array.isArray(syn.citations) && syn.citations.length > 0) {
    lines.push('', `Citações: ${syn.citations.map((c) => `[[${c.path}]]${c.heading ? ` ${c.heading}` : ''}`).join(' · ')}`);
  }
  if (syn.gap) lines.push('', `Lacunas do vault: ${syn.gap}`);
  const out = lines.join('\n');
  return out.length > cap ? `${out.slice(0, cap - 1)}…` : out;
}

// E3 (self-improve-hardening) — search_brain só injeta hits ACIMA do limiar de
// utilidade. Um hit de baixa relevância (score abaixo do piso) é poluição de
// contexto, não sinal — cortá-lo é um GATE, não diagnóstico. Conservador: piso
// baixo (só corta o claramente irrelevante) e hit SEM score é mantido (defensivo,
// nunca derruba contexto não-pontuado). Os vizinhos por wikilink já vêm ≥0.45.
export const BRAIN_USEFULNESS_FLOOR = 0.2;
export function filterBrainHitsByUsefulness(results, floor = BRAIN_USEFULNESS_FLOOR) {
  if (!Array.isArray(results)) return [];
  return results.filter((r) => !(r && typeof r.score === 'number') || r.score >= floor);
}

// formatCorpusBrainHits(results, { cap }) — formata hits do índice LOCAL do
// vault (sprint apolo-corpus) pro resultado do tool. O cap explícito espelha
// MAX_TOOL_RESULT_CHARS: é o chokepoint do InjectionBudget no caminho
// client-side (a LEI "todo chunk injetado é bounded" vale aqui).
export function formatCorpusBrainHits(results, { cap = 4000 } = {}) {
  const lines = ["🧠 Pedro's Brain — índice local (semântico, nada saiu do seu Mac):"];
  for (const r of results || []) {
    const head = r.heading && !String(r.heading).startsWith('∅') ? ` · ${r.heading}` : '';
    const via = String(r.source || '').startsWith('wikilink:')
      ? ` · via [[${String(r.source).slice('wikilink:'.length)}]]`
      : '';
    lines.push(`\n— ${r.title || r.path} (${r.path}${head} · score ${r.score}${via})`);
    const body = String(r.chunk || '').trim();
    if (body) lines.push(body);
  }
  const out = lines.join('\n');
  return out.length > cap ? `${out.slice(0, cap - 1)}…` : out;
}

// runBrainSearch(api, query, { cap? }) — graph-augmented retrieval.
// 1. obsidian_search → primary hits.
// 2. hits exist → one-hop expansion: for the top ≤3 hits, follow [[wikilinks]]
//    (≤5 neighbors total), read each note (defensive), dedup vs primary, append
//    under a "Relacionado (1 hop)" label. RRF-lite: primary outranks neighbors.
// 3. hits empty → ONE fallback: obsidian_get_context; if usable, return it labeled
//    + buildBrainSteering; else steering ALONE. NEVER the bare "(sem resultados)".
// All extra MCP calls are wrapped so a failure NEVER throws. Bounded throughout.
export async function runBrainSearch(api, query, { cap = 5000 } = {}) {
  let primaryRes;
  try {
    primaryRes = await api.mcp.call('obsidian', 'obsidian_search', { query, limit: 8 });
  } catch (e) {
    return `Erro na busca do brain: ${e?.message || String(e)}. Verifique o servidor obsidian na aba Brain.`;
  }

  const hits = parseBrainHits(primaryRes);

  // ── Empty primary → steering off-ramp (anti-flail) ──
  if (hits.length === 0) {
    let ctx = '';
    try {
      const ctxRes = await api.mcp.call('obsidian', 'obsidian_get_context', { query });
      ctx = brainExcerpt(ctxRes, 1500);
    } catch { ctx = ''; }
    const steering = buildBrainSteering(query);
    // GAP 3: the vault returns a "nothing found" sentinel from get_context — don't
    // dress that up as "notas próximas"; it's not a related note, it's a no-op.
    if (ctx && !isNothingFoundSentinel(ctx)) {
      return truncate(`— Sem match exato; notas próximas —\n${ctx}\n\n${steering}`, cap);
    }
    return steering;
  }

  // ── Primary hits → render + one-hop neighbor expansion ──
  const primaryBlocks = hits.map((h) => ({
    key: h.key,
    text: `${h.title}${h.date ? ` (${h.date})` : ''}\n${h.body}`,
  }));
  // Cross-tier dedup set: include BOTH the path-key (path|title) AND a name-key
  // (basename sans .md + the lowercased title) so a [[wikilink]] target — which is
  // name-shaped — matches a primary whose key is path-shaped. Without the name-key,
  // 'brain/notas/outra.md' never equals 'outra' and the note got re-fetched.
  const primaryKeys = new Set();
  for (const h of hits) {
    if (h.key) primaryKeys.add(h.key);
    const nameKey = pathBasenameKey(h.path);
    if (nameKey) primaryKeys.add(nameKey);
    const titleKey = String(h.title || '').trim().toLowerCase();
    if (titleKey) primaryKeys.add(titleKey);
  }

  // GAP 1: on REAL vault data the search `summary` (h.body) is PROSE with NO
  // [[wikilinks]] — the links live in the note's FULL `content` (e.g. a
  // `## Relacionado` section) that only obsidian_read_note(path) returns. So
  // fetch the FULL CONTENT of the top ≤2 primaries (defensive, never throws,
  // bounded) and extract wikilinks from THAT, falling back to the summary body
  // when read_note yields nothing. Without this, expansion was always 0 neighbors.
  const linkSources = [];
  for (const h of hits.slice(0, 2)) {
    let content = '';
    if (h.path) {
      try {
        const noteRes = await api.mcp.call('obsidian', 'obsidian_read_note', { path: h.path });
        content = brainExcerpt(noteRes, 5000);
      } catch { content = ''; }
    }
    linkSources.push(content || h.body);
  }
  // Hits beyond the top 2 still contribute links from their summary body (no read).
  for (const h of hits.slice(2, 3)) linkSources.push(h.body);

  // Collect unique neighbor link names from those sources, capped at 5 total.
  const neighborNames = [];
  const neighborSeen = new Set();
  for (const src of linkSources) {
    for (const link of extractWikilinks(src)) {
      const lk = link.trim().toLowerCase();
      if (!lk || neighborSeen.has(lk)) continue;
      if (primaryKeys.has(lk)) continue; // link points at a note we already have
      neighborSeen.add(lk);
      neighborNames.push(link);
      if (neighborNames.length >= 5) break;
    }
    if (neighborNames.length >= 5) break;
  }

  const neighborBlocks = [];
  for (const name of neighborNames) {
    const excerpt = await readLinkedNote(api, name); // never throws
    if (!excerpt) continue;
    const nk = name.trim().toLowerCase();
    if (primaryKeys.has(nk)) continue; // dedup vs primary by name/path|title
    neighborBlocks.push({ key: nk, text: `${name}\n${excerpt}` });
  }

  let body;
  if (neighborBlocks.length) {
    const primaryText = rankBrainBlocks(primaryBlocks, []);
    const neighborText = rankBrainBlocks([], neighborBlocks);
    body = `${primaryText}\n\n— Relacionado (1 hop no grafo do vault) —\n${neighborText}`;
  } else {
    body = rankBrainBlocks(primaryBlocks, []);
  }
  return truncate(body, cap) || buildBrainSteering(query);
}

// Normalize an obsidian MCP result into readable text for the model. MCP tools
// return { content: [{ type:'text', text }] }; some bridges return { results }
// or a raw string. Handles all three. For { results }, it cleans frontmatter/
// whitespace noise, dedups overlapping hits by path|title, and drops notes whose
// body is empty after cleaning — so the model gets tight, signal-only excerpts.
export function formatBrainResult(res) {
  if (res == null) return '';
  if (typeof res === 'string') return res;
  if (Array.isArray(res.content)) {
    return res.content.map((b) => (typeof b === 'string' ? b : b?.text || '')).filter(Boolean).join('\n');
  }
  if (Array.isArray(res.results)) {
    const seen = new Set();
    const blocks = [];
    for (const r of res.results) {
      if (!r) continue;
      const heading = r.title || r.path || '?';
      const key = (r.path || r.title || '').trim().toLowerCase();
      if (key && seen.has(key)) continue; // dedup overlapping search hits
      const body = cleanBrainSnippet(r.summary || r.text || '');
      if (!body) continue; // drop orphan titles with no usable body
      if (key) seen.add(key);
      blocks.push(`${heading}${r.date ? ` (${r.date})` : ''}\n${body}`);
    }
    return blocks.join('\n\n');
  }
  try { return JSON.stringify(res); } catch { return String(res); }
}
